class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-goose-discount], [data-goose-add]')) return;
      event.preventDefault();
      if (event.target.matches('[data-goose-add]')) this.addAccessory(event.target);
      else this.applyDiscount(event.target);
    });
    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-goose-accessory-variant]')) return;
      const control = event.target;
      const card = control.closest('.goose-cart-recommendation');
      const option = control.matches('select') ? control.selectedOptions[0] : control;
      control.form.querySelector('button[type="submit"]').disabled = !control.value || !!CartItems.changePending;
      if (option?.dataset.price) card.querySelector('[data-goose-accessory-price]').textContent = option.dataset.price;
      if (option?.dataset.image) {
        const image = card.querySelector('img');
        const choiceImage = control.closest('label')?.querySelector('img');
        image.removeAttribute('srcset');
        image.src = choiceImage?.currentSrc || option.dataset.image;
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const pendingStyles = Array.from(document.querySelectorAll('link[data-goose-cart-style]'))
      .filter((link) => !link.sheet && link.dataset.gooseCartStyleLoaded !== 'true');
    if (pendingStyles.length) {
      if (this.styleLoadPromise) return;
      triggeredBy?.setAttribute('aria-busy', 'true');
      this.styleLoadPromise = Promise.all(pendingStyles.map((link) => new Promise((resolve) => {
        const ready = () => {
          link.dataset.gooseCartStyleLoaded = 'true';
          resolve();
        };
        link.addEventListener('load', ready, { once: true });
        link.addEventListener('error', ready, { once: true });
        link.media = 'all';
      }))).finally(() => {
        this.styleLoadPromise = null;
        triggeredBy?.removeAttribute('aria-busy');
        if (this.isConnected) this.open(triggeredBy);
      });
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.prioritizeImages();
    this.prepareAccessoryImages();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      const container = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : this.querySelector('.drawer__inner');
      trapFocus(container, container.querySelector('.drawer__close') || container);
    });

    document.body.classList.add('overflow-hidden');
  }

  prioritizeImages() {
    this.querySelectorAll('.cart-item__image').forEach((image, index) => {
      if (index >= 3) return;
      image.fetchPriority = index < 2 ? 'high' : 'auto';
      image.loading = 'eager';
    });
  }

  prepareAccessoryImages() {
    this.querySelector('goose-cart-extras')?.primeImages?.();
    this.querySelectorAll('.goose-cart-recommendation img').forEach(image => {
      image.fetchPriority = 'low';
      image.decoding = 'async';
      image.loading = 'eager';
    });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, shouldOpen = true, focusSelector = '[data-goose-discount-status]:not(:empty)') {
    const drawerHTML = parsedState.sections?.['cart-drawer'];
    if (!drawerHTML) throw new Error(this.dataset.error);
    const sourceDrawer = new DOMParser().parseFromString(drawerHTML, 'text/html').querySelector('cart-drawer');
    if (!sourceDrawer) throw new Error(this.dataset.error);
    this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    if (shouldOpen || this.classList.contains('active')) {
      this.prioritizeImages();
      this.prepareAccessoryImages();
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (shouldOpen) this.open();
      else if (this.classList.contains('active')) {
        const container = this.querySelector('.drawer__inner');
        trapFocus(container, this.querySelector(focusSelector) || container.querySelector('.drawer__close'));
      }
    });
  }

  async refresh(shouldOpen = false, focusSelector) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, { cache: 'no-store' });
    if (!response.ok) throw new Error(this.dataset.error);
    this.renderContents({ sections: await response.json() }, shouldOpen, focusSelector);
  }

  async addAccessory(form) {
    if (CartItems.changePending || !form.reportValidity()) return;
    const variantId = Number(form.elements.id.value);
    if (!Number.isSafeInteger(variantId) || variantId <= 0) return;
    CartItems.changePending = true;
    const button = form.querySelector('button[type="submit"]');
    const originalText = button.textContent;
    const controls = [...document.querySelectorAll('cart-drawer .quantity__button, cart-drawer .quantity__input, cart-drawer cart-remove-button button, [name="checkout"], [data-goose-discount] button, [data-goose-add] button, [data-goose-add] select, [data-goose-add] input')].map((control) => ({ control, disabled: control.disabled }));
    controls.forEach(({ control }) => { control.disabled = true; });
    form.setAttribute('aria-busy', 'true');
    button.textContent = this.dataset.adding;
    const focusSelector = '[data-goose-accessory-status]';
    let confirmed = false;
    let knownError = false;
    let message = this.dataset.addUncertain;
    let failed = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        ...fetchConfig(),
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: this.getSectionsToRender().map((section) => section.id), sections_url: window.location.pathname })
      });
      const state = await response.json();
      if (!response.ok || state.status || state.errors) {
        knownError = response.status === 422;
        message = knownError && typeof state.description === 'string' ? state.description : this.dataset.addError;
        throw new Error(message);
      }
      confirmed = true;
      try {
        this.renderContents(state, false, focusSelector);
      } catch (error) {
        await this.refresh(false, focusSelector);
      }
      message = this.dataset.added;
      failed = false;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'goose-accessory', cartData: state });
    } catch (error) {
      if (confirmed) {
        message = this.dataset.addRefreshError;
      } else {
        if (!knownError) message = this.dataset.addUncertain;
        try { await this.refresh(false, focusSelector); } catch (refreshError) {}
      }
    } finally {
      CartItems.changePending = false;
      controls.forEach(({ control, disabled }) => { if (control.isConnected) control.disabled = disabled; });
      if (form.isConnected) {
        form.removeAttribute('aria-busy');
        button.textContent = originalText;
        if (confirmed) button.disabled = true;
      }
      const status = this.querySelector(focusSelector);
      if (status) {
        status.textContent = message;
        status.toggleAttribute('data-error', failed);
        status.focus();
      }
    }
  }

  async applyDiscount(form) {
    if (CartItems.changePending) return;
    const code = form.elements.discount.value.trim();
    if (!code) return;
    CartItems.changePending = true;
    const buttons = [...this.querySelectorAll('.quantity__button, cart-remove-button button, [name="checkout"], [data-goose-discount] button')].map((button) => ({ button, disabled: button.disabled }));
    buttons.forEach(({ button }) => { button.disabled = true; });
    let message = this.dataset.error;
    let failed = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const currentResponse = await fetch(`${root}cart.js`, { cache: 'no-store' });
      if (!currentResponse.ok) throw new Error(message);
      const current = await currentResponse.json();
      const codes = [...new Set([...(current.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code), code])];
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ discount: codes.join(','), sections: this.getSectionsToRender().map((section) => section.id), sections_url: window.location.pathname })
      });
      const state = await response.json();
      if (!response.ok || state.errors) throw new Error(message);
      this.renderContents(state, false);
      const applied = (state.discount_codes || []).some((entry) => entry.code.toLowerCase() === code.toLowerCase() && entry.applicable);
      message = applied ? this.dataset.applied : this.dataset.invalid;
      failed = !applied;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'goose-discount', cartData: state });
    } catch (error) {
      message = this.dataset.error;
    } finally {
      CartItems.changePending = false;
      buttons.forEach(({ button, disabled }) => { button.disabled = disabled; });
      const status = this.querySelector('[data-goose-discount-status]');
      if (status) {
        status.textContent = message;
        status.toggleAttribute('data-error', failed);
        status.focus();
      }
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    this.closest('cart-drawer')?.refresh(false).catch(console.error);
  }
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
